import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Typography, Box, Paper, Divider, TextField, Button, Stack, 
    Grid, Switch, FormControlLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppStore } from '../../../../../store/appStore.tsx';
import { useValue } from '../../../../../context/ContextProvider';
import { updateConfigAlarma } from '../../../../../actions/alarmaActions';


// --- Componente Reutilizable para Input de Tiempo (MM:SS) ---
const TimeInputMS = ({ label, name, value = { m: 0, s: 0 }, onChange }) => {
    const handleComponentChange = (component) => (event) => {
        let val = parseInt(event.target.value, 10);
        if (isNaN(val)) val = 0;
        val = Math.max(0, Math.min(val, 59));
        const updatedTime = { ...value, [component]: val };
        const syntheticEvent = { target: { name, value: updatedTime, type: 'time_object' } };
        onChange(syntheticEvent);
    };
    return (
        <Box sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1, p: 2, position: 'relative' }}>
            <Typography variant="body2" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1 }}>{label}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ pt: 1 }}>
                <TextField label="MM" type="number" value={String(value.m || 0).padStart(2, '0')} onChange={handleComponentChange('m')} size="small" sx={{ width: 70 }} />
                <Typography variant="h6">:</Typography>
                <TextField label="SS" type="number" value={String(value.s || 0).padStart(2, '0')} onChange={handleComponentChange('s')} size="small" sx={{ width: 70 }} />
            </Stack>
        </Box>
    );
};

// --- Generador del estado inicial para el padre ---
export const generarEstadoInicialSirena = () => ({
    hab: 1, // El JSON usa 1 o 0
    dlyAntes: 10,
    tiempoSonido: 60,
    sleepSonido: 120,
    ciclos: 20
});

// --- Funciones de ayuda ---
const msToTotalSeconds = (timeObj) => (timeObj.m || 0) * 60 + (timeObj.s || 0);
const createViewStateFromConfig = (config) => {
    if (!config) return {};
    const secondsToComponents = (totalSeconds = 0) => ({
        m: Math.floor(totalSeconds / 60),
        s: totalSeconds % 60,
    });
    return {
        hab: !!config.hab, // El estado de la vista usa un booleano para el Switch
        dlyAntes: secondsToComponents(config.dlyAntes),
        tiempoSonido: secondsToComponents(config.tiempoSonido),
        sleepSonido: secondsToComponents(config.sleepSonido),
        ciclos: config.ciclos,
    }
};

// --- El Componente del Formulario ---
function ConfiguracionSirenaForm({ config, setConfig, state, setState, originalConfigRef }) {
    const { jsonOutput, formDirty, jsonNeedsUpdate } = state;
    const theme = useTheme();
    const { currentUser, device, startLoading, endLoading } = useAppStore();
    const { dispatch } = useValue();

    const [viewState, setViewState] = useState(() => createViewStateFromConfig(config));

    useEffect(() => {
        setViewState(createViewStateFromConfig(config));
    }, [config]);
    
    useEffect(() => {
        const originalConfigString = JSON.stringify(originalConfigRef.current);
        const currentConfigString = JSON.stringify(config);
        const isDirty = originalConfigString !== currentConfigString;
        
        let needsUpdate = true;
        try {
            const jsonObject = JSON.parse(jsonOutput || '{}');
            needsUpdate = JSON.stringify(jsonObject) !== currentConfigString;
        } catch (e) { needsUpdate = true; }

        if (isDirty !== formDirty || needsUpdate !== jsonNeedsUpdate) {
            setState(prev => ({ ...prev, formDirty: isDirty, jsonNeedsUpdate: needsUpdate }));
        }
    }, [config, jsonOutput, formDirty, jsonNeedsUpdate, originalConfigRef, setState]);
    
    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const valorFinal = type === 'checkbox' ? checked : type === 'time_object' ? value : parseInt(value, 10) || 0;
        
        const newViewState = { ...viewState, [name]: valorFinal };
        setViewState(newViewState);

        // Convierte el nuevo estado de la vista al formato del padre y lo actualiza
        const newConfig = {
            hab: newViewState.hab ? 1 : 0, // <-- CAMBIO AQUÍ: Convierte booleano a 1 o 0
            dlyAntes: msToTotalSeconds(newViewState.dlyAntes),
            tiempoSonido: msToTotalSeconds(newViewState.tiempoSonido),
            sleepSonido: msToTotalSeconds(newViewState.sleepSonido),
            ciclos: newViewState.ciclos,
        };
        setConfig(newConfig);
    };

    const handleGenerateJson = () => {
        setState(prev => ({
            ...prev,
            jsonOutput: JSON.stringify(config, null, 2),
            jsonNeedsUpdate: false
        }));
    };

    const sendConfig = async() => {
        /*//console.log("--- Enviando Configuración GPRS JSON ---", config);
        alert("Configuración GPRS enviada. Revisa la consola.");
        originalConfigRef.current = JSON.parse(JSON.stringify(config));
        setState(prev => ({ ...prev, formDirty: false }));
        */
        //console.log("--- Enviando Configuración JSON ---", config);
        startLoading();
        setState(prev => ({ ...prev, configId: "---", estado: "Esperando respuesta del servidor/dispositivo", color: "black"}));
        const updateResult = await updateConfigAlarma(currentUser, device.devId, "CMD_SET_CONFIG_SIRENA", config, dispatch);
        //console.log("updateResult: ", updateResult);

        if (updateResult?.deviceResponse === 0) {
            // Si el envío es exitoso, actualizamos la configuración original
            originalConfigRef.current = JSON.parse(JSON.stringify(config));
            setState(prev => ({ ...prev, formDirty: false, configId: updateResult.insertId, estado: "Última configuración actualizada con éxito.", color: "green"}));
            alert(`Configuración enviada con éxito (ID: ${updateResult.insertId})`);
        } else {
            setState(prev => ({ ...prev, configId: 0, estado: "Error. Última configuración NO actualizada.", color: "red"}));
            alert("Error al enviar la configuración.");
        }
        endLoading();
    };

    const handleCancel = () => {
        if (window.confirm("¿Seguro que quieres descartar los cambios?")) {
            setConfig(originalConfigRef.current);
        }
    };
    
    const formatMs = (timeObj) => {
        if (!timeObj) return '00:00';
        return `${String(timeObj.m || 0).padStart(2, '0')}:${String(timeObj.s || 0).padStart(2, '0')}`;
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>Configuración de Sirena</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={(e) => { e.preventDefault(); sendConfig(); }}>
                    <Grid container spacing={4}>
                        {/* Columna izquierda: Formulario */}
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <FormControlLabel control={<Switch name="hab" checked={!!viewState.hab} onChange={handleChange} />} label="Habilitar Sirena" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TimeInputMS label="Delay antes de sonar" name="dlyAntes" value={viewState.dlyAntes} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TimeInputMS label="Duración Sonido" name="tiempoSonido" value={viewState.tiempoSonido} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TimeInputMS label="Pausa Sonido" name="sleepSonido" value={viewState.sleepSonido} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth type="number" name="ciclos" label="Ciclos" value={viewState.ciclos} onChange={handleChange} inputProps={{ min: 0, max: 9999 }}/>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Columna derecha: JSON y Resumen */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                           <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>JSON DE CONFIGURACIÓN</Typography>
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', border: `1px solid ${jsonNeedsUpdate ? theme.palette.warning.main : theme.palette.divider}`, borderRadius: 1, p: 1.5, backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'background.paper', minHeight: '200px' }}>
                                    <Typography component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', m: 0, fontSize: '0.875rem' }}>
                                        {jsonOutput}
                                    </Typography>
                                </Box>
                                <Button fullWidth variant="contained" color={jsonNeedsUpdate ? "warning" : "primary"} onClick={handleGenerateJson} sx={{ mt: 1 }}>
                                    {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                                </Button>
                            </Paper>
                            <Paper variant="outlined" sx={{ p: 2, position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{ position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold' }}>RESUMEN DE CONFIGURACIÓN</Typography>
                                <Typography variant="body2">**Habilitado:** {viewState.hab ? 'Sí' : 'No'}</Typography>
                                <Typography variant="body2">**Delay antes de sonar:** {formatMs(viewState.dlyAntes)} (MM:SS)</Typography>
                                <Typography variant="body2">**Duración Sirena:** {formatMs(viewState.tiempoSonido)} (MM:SS)</Typography>
                                <Typography variant="body2">**Pausa Sirena:** {formatMs(viewState.sleepSonido)} (MM:SS)</Typography>
                                <Typography variant="body2">**Ciclos:** {viewState.ciclos}</Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color={formDirty ? 'error' : 'inherit'}>**Formulario Modificado:** {formDirty ? 'Sí' : 'No'}</Typography>
                                    <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'inherit'}>**JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}</Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Fila inferior: Resultados del envio de la configuracion al dispositivo */}
                        <Grid item xs={12} md={12}>
                            <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                                <Typography variant="caption" component="div" sx={{position: 'absolute', top: 0, left: 12, transform: 'translateY(-50%)', bgcolor: 'background.paper', px: 1, color: 'primary.main', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase'}}>
                                    Resultado envío al Dispositivo
                                </Typography>
                                <Box>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5 }}>
                                        <strong>ConfigId: </strong>{state.configId}
                                    </Typography>
                                    <Typography variant="body2" component="p" sx={{ ml: 2, fontFamily: 'monospace', mb: 0.5, color: state.color }}>
                                        <strong>Estado: </strong>{state.estado}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        
                        {/* Botones de Acción */}
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={!formDirty}>Cancelar</Button>
                                <Button type="submit" variant="contained" disabled={jsonNeedsUpdate}>Enviar</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracionSirenaForm;